import React, { useEffect, useMemo, useState } from "react";
import { AppShell } from "../components/Layout";
import { api, formatINR, blurOnWheel } from "../lib/api";
import { useAuth, useHasPermission } from "../context/AuthContext";
import { toast } from "sonner";
import { Wallet, Calendar, ListChecks, History as HistoryIcon, AlertTriangle, CheckCircle2, ChevronRight, Coins, ClipboardCheck, Landmark, Settings as SettingsIcon, Download } from "lucide-react";

// ─────────────────────────────────────────────────────────────────────────
// Phase C3 Part 3 — PDF export helper (server-side ReportLab). Downloads the
// blob via the authenticated axios instance so the confidential seal is
// preserved (a non-permitted user gets 403, no fallback path).
// ─────────────────────────────────────────────────────────────────────────
async function downloadPdf(path, filename) {
  try {
    const res = await api.get(path, { responseType: "blob" });
    const blob = new Blob([res.data], { type: "application/pdf" });
    // Prefer the server-supplied filename from Content-Disposition when present.
    const cd = res.headers?.["content-disposition"] || res.headers?.["Content-Disposition"];
    let name = filename;
    if (cd) {
      const m = /filename="?([^"]+)"?/i.exec(cd);
      if (m) name = m[1];
    }
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = name || "report.pdf";
    document.body.appendChild(a); a.click();
    setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 100);
    toast.success("PDF downloaded");
  } catch (e) {
    toast.error(e?.response?.data?.detail || "Export failed");
  }
}

function ExportPdfButton({ path, filename, label = "Export PDF", testid }) {
  const [busy, setBusy] = useState(false);
  const onClick = async () => {
    setBusy(true);
    try { await downloadPdf(path, filename); }
    finally { setBusy(false); }
  };
  return (
    <button onClick={onClick} disabled={busy}
            data-testid={testid || "export-pdf"}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border border-slate-300 text-slate-700 bg-white hover:bg-slate-50 disabled:opacity-50">
      <Download size={12} />
      {busy ? "Exporting…" : label}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Phase C3 — shared date-window helpers used by every date input across the
// Cash Flow module (sales, counts, deposits) so the UX cannot drift between
// forms. Server-side is the sole enforcer; these are UX niceties.
// ─────────────────────────────────────────────────────────────────────────
export function useDateWindow() {
  const [cfg, setCfg] = useState({ backdate_days: 10, today_ist: null });
  useEffect(() => {
    api.get("/date-window/config").then((r) => setCfg(r.data)).catch(() => {});
  }, []);
  const today = cfg.today_ist || new Date().toISOString().slice(0, 10);
  const daysAgo = (dateStr) => {
    if (!dateStr) return 0;
    const t = new Date(today + "T00:00:00").getTime();
    const d = new Date(dateStr + "T00:00:00").getTime();
    return Math.round((t - d) / 86400000);
  };
  return { ...cfg, today, daysAgo };
}

/** UI-hint state for a date input against the C3 rules. */
export function dateWindowState(dateStr, dw) {
  if (!dateStr) return { kind: "empty" };
  const days = dw.daysAgo(dateStr);
  if (days < 0) return { kind: "future" };
  if (days > (dw.backdate_days || 10)) return { kind: "beyond", days, beyond: days - (dw.backdate_days || 10) };
  return { kind: "ok", days };
}

/** Inline hint below a date input. */
function DateWindowHint({ state, isAdmin }) {
  if (!state || state.kind === "empty" || state.kind === "ok") return null;
  if (state.kind === "future") {
    return <div className="mt-1 text-[11px] text-red-600 font-semibold">Future date — will be rejected.</div>;
  }
  if (state.kind === "beyond") {
    return (
      <div className={`mt-1 text-[11px] font-semibold ${isAdmin ? "text-amber-700" : "text-red-600"}`}>
        {state.days} days ago · beyond back-date window{isAdmin ? " · admin override required" : " · not permitted for non-admins"}.
      </div>
    );
  }
  return null;
}

/** Modal that collects a mandatory reason for admin back-date override. */
function OverrideReasonDialog({ open, onCancel, onConfirm, entryLabel, dateLabel }) {
  const [reason, setReason] = useState("");
  useEffect(() => { if (open) setReason(""); }, [open]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="override-dialog">
      <div className="bg-white rounded-2xl border border-slate-200 w-full max-w-md p-5 space-y-3">
        <div>
          <div className="font-display font-black text-lg">Admin back-date override</div>
          <div className="text-xs text-slate-500 mt-1">
            {entryLabel} is dated <span className="font-semibold text-slate-800">{dateLabel}</span> — outside the back-date window. A reason is required and will be permanently recorded on the entry and in the audit log.
          </div>
        </div>
        <textarea
          data-testid="override-reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          placeholder="Reason (e.g. Missed sales from remote site, bank slip received late…)"
          className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-olive/30"
        />
        <div className="flex justify-end gap-2 pt-1">
          <button onClick={onCancel} data-testid="override-cancel"
                  className="px-4 py-2 rounded-lg text-xs font-semibold text-slate-600 hover:bg-slate-100">
            Cancel
          </button>
          <button
            onClick={() => onConfirm(reason.trim())}
            disabled={!reason.trim()}
            data-testid="override-confirm"
            className="bg-olive text-white px-4 py-2 rounded-lg text-xs font-semibold hover:bg-olive-700 disabled:opacity-40"
          >
            Record with override
          </button>
        </div>
      </div>
    </div>
  );
}


/**
 * Phase C1 — Cash Flow (Daily Sales + T+1 Receipts Reconciliation)
 *
 * Sealed behind `manage_cashflow`. Renders a soft 404 for anyone else so the
 * feature stays invisible to non-permitted users.
 *
 * Three internal tabs (all under /cashflow route):
 *   • Entry     — record today's/any day's sales per site, per revenue point
 *   • Reconcile — days awaiting receipts; drill in and fill actuals
 *   • History   — past days table with Diffs + flags
 */
export default function CashFlowPage() {
  const canView = useHasPermission("manage_cashflow");
  const [tab, setTab] = useState("summary");

  if (!canView) {
    return (
      <AppShell>
        <div className="max-w-md mx-auto text-center py-24" data-testid="cashflow-denied">
          <div className="text-sm text-slate-500">Not found</div>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto space-y-6" data-testid="cashflow-page">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl bg-olive/10 grid place-items-center">
            <Wallet size={20} strokeWidth={2} className="text-olive" />
          </div>
          <div>
            <h1 className="font-display text-2xl font-black tracking-tight">Cash Flow</h1>
            <div className="text-xs text-slate-500 mt-0.5">Daily sales · T+1 gateway reconciliation</div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-slate-200 -mt-2 flex-wrap">
          {[
            { id: "summary",   label: "Summary",   Icon: Coins },
            { id: "entry",     label: "Sales",     Icon: Calendar },
            { id: "reconcile", label: "Reconcile", Icon: ListChecks },
            { id: "deposits",  label: "Deposits",  Icon: Landmark },
            { id: "bank",      label: "Bank",      Icon: Landmark },
            { id: "history",   label: "History",   Icon: HistoryIcon },
            { id: "settings",  label: "Settings",  Icon: SettingsIcon },
          ].map(({ id, label, Icon }) => {
            const active = tab === id;
            return (
              <button
                key={id}
                data-testid={`cashflow-tab-${id}`}
                onClick={() => setTab(id)}
                className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-semibold border-b-2 -mb-[2px] transition ${
                  active ? "border-olive text-olive" : "border-transparent text-slate-500 hover:text-slate-800"
                }`}
              >
                <Icon size={14} strokeWidth={2} />
                {label}
              </button>
            );
          })}
        </div>

        {tab === "summary" && <PositionTab />}
        {tab === "entry" && <EntryTab />}
        {tab === "reconcile" && <ReconcileTab />}
        {tab === "deposits" && <DepositsTab />}
        {tab === "bank" && <BankTab />}
        {tab === "history" && <HistoryTab />}
        {tab === "settings" && <SettingsTab />}
      </div>
    </AppShell>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Shared helpers
// ═══════════════════════════════════════════════════════════════════════════
function todayISO() {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

const NumberInput = ({ value, onChange, testid, disabled }) => (
  <input
    type="number"
    inputMode="decimal"
    min="0"
    step="0.01"
    onWheel={blurOnWheel}
    onKeyDown={(e) => { if (e.key === "ArrowUp" || e.key === "ArrowDown") e.preventDefault(); }}
    value={value === 0 ? "" : value}
    onChange={(e) => onChange(e.target.value === "" ? 0 : Number(e.target.value))}
    disabled={disabled}
    placeholder="0"
    data-testid={testid}
    className="w-full px-2 py-1.5 text-right rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-olive/30 disabled:bg-slate-50 disabled:text-slate-500"
  />
);

// ═══════════════════════════════════════════════════════════════════════════
// ENTRY TAB — record daily sales
// ═══════════════════════════════════════════════════════════════════════════
function EntryTab() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const dw = useDateWindow();
  const [date, setDate] = useState(todayISO());
  const [config, setConfig] = useState(null);
  const [dayDocs, setDayDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});   // {site: bool}
  const [drafts, setDrafts] = useState({});
  // Phase C3 — override dialog scoped to a site (when saving a beyond-window date).
  const [overrideSite, setOverrideSite] = useState(null);
  const dateState = dateWindowState(date, dw);

  const load = async (theDate) => {
    setLoading(true);
    try {
      const [cfg, day] = await Promise.all([
        api.get("/cashflow/config"),
        api.get(`/cashflow/sales?date=${theDate}`),
      ]);
      setConfig(cfg.data);
      setDayDocs(day.data);
      // Seed drafts from existing entries + configured blanks.
      const nextDrafts = {};
      for (const doc of day.data) {
        const site = doc.site;
        nextDrafts[site] = {};
        const isMultiPoint = !!(cfg.data.points_by_site || {})[site];
        if (isMultiPoint) {
          const points = cfg.data.points_by_site[site];
          const byPoint = Object.fromEntries((doc.lines || []).map((ln) => [ln.revenue_point, ln]));
          for (const p of points) {
            const existing = byPoint[p];
            nextDrafts[site][p] = {
              cash: existing?.cash ?? 0,
              online: existing?.online ?? 0,
            };
          }
        } else {
          const existing = (doc.lines || [])[0];
          nextDrafts[site]["__single__"] = {
            cash: existing?.cash ?? 0,
            online: existing?.online ?? 0,
          };
        }
      }
      setDrafts(nextDrafts);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(date); }, [date]);

  const totals = useMemo(() => {
    let cash = 0, online = 0;
    for (const site of Object.keys(drafts)) {
      for (const k of Object.keys(drafts[site])) {
        cash += Number(drafts[site][k].cash) || 0;
        online += Number(drafts[site][k].online) || 0;
      }
    }
    return { cash, online, grand: cash + online };
  }, [drafts]);

  const updateCell = (site, key, field, value) => {
    setDrafts((d) => ({
      ...d,
      [site]: {
        ...(d[site] || {}),
        [key]: { ...(d[site]?.[key] || { cash: 0, online: 0 }), [field]: value },
      },
    }));
  };

  const doSaveSite = async (siteName, overrideReason = null) => {
    const isMultiPoint = !!(config.points_by_site || {})[siteName];
    const siteDoc = dayDocs.find((d) => d.site === siteName);
    if (siteDoc?.receipts_started) {
      toast.error("Locked — receipts already started. Ask an admin to edit.");
      return;
    }
    let lines;
    if (isMultiPoint) {
      const points = config.points_by_site[siteName];
      lines = points.map((p) => ({
        revenue_point: p,
        cash: Number(drafts[siteName]?.[p]?.cash) || 0,
        online: Number(drafts[siteName]?.[p]?.online) || 0,
      }));
    } else {
      const d = drafts[siteName]?.["__single__"] || {};
      lines = [{ revenue_point: null, cash: Number(d.cash) || 0, online: Number(d.online) || 0 }];
    }
    setSaving((s) => ({ ...s, [siteName]: true }));
    try {
      await api.post("/cashflow/sales", {
        sale_date: date, site: siteName, lines,
        ...(overrideReason ? { admin_override: true, override_reason: overrideReason } : {}),
      });
      toast.success(`Saved · ${siteName}` + (overrideReason ? " (back-date override applied)" : ""));
      await load(date);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error((detail && typeof detail === "object" ? detail.message : detail) || "Save failed");
    } finally { setSaving((s) => ({ ...s, [siteName]: false })); }
  };

  const saveSite = (siteName) => {
    if (dateState.kind === "future") { toast.error("Future dates are not allowed."); return; }
    if (dateState.kind === "beyond") {
      if (!isAdmin) { toast.error("This date is beyond the back-date window. Ask an admin."); return; }
      setOverrideSite(siteName); return;
    }
    doSaveSite(siteName);
  };

  if (loading) return <div className="text-sm text-slate-500">Loading…</div>;
  if (!config) return null;

  const configuredSites = [
    ...Object.keys(config.points_by_site || {}),
    ...(config.single_line_sites || []),
  ];

  return (
    <div className="space-y-6">
      {/* Date + overall totals */}
      <div className="flex items-end justify-between gap-4 flex-wrap bg-white rounded-2xl border border-slate-200 p-4">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Sales date</div>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            max={dw.today}
            data-testid="entry-date-input"
            className="px-3 py-2 rounded-lg border border-slate-300 text-sm"
          />
          <DateWindowHint state={dateState} isAdmin={isAdmin} />
        </div>
        <div className="flex gap-6" data-testid="entry-day-totals">
          <TotalBlock label="Cash" value={totals.cash} />
          <TotalBlock label="Online" value={totals.online} />
          <TotalBlock label="Grand Total" value={totals.grand} strong />
        </div>
      </div>

      {/* Per-site cards */}
      <div className="space-y-4">
        {configuredSites.map((site) => {
          const isMultiPoint = !!(config.points_by_site || {})[site];
          const siteDoc = dayDocs.find((d) => d.site === site);
          const isLocked = !!siteDoc?.receipts_started;
          const rows = isMultiPoint
            ? (config.points_by_site[site] || []).map((p) => ({ key: p, label: p }))
            : [{ key: "__single__", label: site }];
          const siteCash = rows.reduce((a, r) => a + (Number(drafts[site]?.[r.key]?.cash) || 0), 0);
          const siteOnline = rows.reduce((a, r) => a + (Number(drafts[site]?.[r.key]?.online) || 0), 0);
          return (
            <div key={site} className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid={`entry-card-${site}`}>
              <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
                <div>
                  <div className="font-semibold text-sm">{site}</div>
                  <div className="text-[11px] text-slate-500 mt-0.5">
                    {isMultiPoint ? `${rows.length} revenue points` : "Single line"}
                    {isLocked && <span className="ml-2 text-amber-600 font-semibold">· LOCKED (receipts started)</span>}
                    {siteDoc?.entered && !isLocked && <span className="ml-2 text-olive font-semibold">· Saved</span>}
                  </div>
                </div>
                <button
                  data-testid={`entry-save-${site}`}
                  onClick={() => saveSite(site)}
                  disabled={saving[site] || isLocked}
                  className="bg-olive text-white px-4 py-2 rounded-lg text-xs font-semibold hover:bg-olive-700 disabled:opacity-40 transition"
                >
                  {saving[site] ? "Saving…" : (siteDoc?.entered ? "Update" : "Save")}
                </button>
              </div>
              <div className="p-3">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[10px] uppercase tracking-wider text-slate-500">
                      <th className="text-left px-2 py-1.5 font-semibold">Revenue Point</th>
                      <th className="text-right px-2 py-1.5 font-semibold w-32">Cash</th>
                      <th className="text-right px-2 py-1.5 font-semibold w-32">Online</th>
                      <th className="text-right px-2 py-1.5 font-semibold w-32">Line total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map(({ key, label }) => {
                      const cash = Number(drafts[site]?.[key]?.cash) || 0;
                      const online = Number(drafts[site]?.[key]?.online) || 0;
                      return (
                        <tr key={key} className="border-t border-slate-100">
                          <td className="px-2 py-1.5 text-slate-700">{label}</td>
                          <td className="px-2 py-1.5">
                            <NumberInput
                              value={cash}
                              onChange={(v) => updateCell(site, key, "cash", v)}
                              testid={`entry-cell-cash-${site}-${key}`}
                              disabled={isLocked}
                            />
                          </td>
                          <td className="px-2 py-1.5">
                            <NumberInput
                              value={online}
                              onChange={(v) => updateCell(site, key, "online", v)}
                              testid={`entry-cell-online-${site}-${key}`}
                              disabled={isLocked}
                            />
                          </td>
                          <td className="px-2 py-1.5 text-right font-mono text-slate-700 text-xs">
                            {formatINR(cash + online)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="border-t border-slate-200 font-semibold text-xs">
                      <td className="px-2 py-1.5 text-slate-500 uppercase tracking-wider text-[10px]">Site total</td>
                      <td className="px-2 py-1.5 text-right font-mono">{formatINR(siteCash)}</td>
                      <td className="px-2 py-1.5 text-right font-mono">{formatINR(siteOnline)}</td>
                      <td className="px-2 py-1.5 text-right font-mono">{formatINR(siteCash + siteOnline)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          );
        })}
      </div>
      <OverrideReasonDialog
        open={!!overrideSite}
        onCancel={() => setOverrideSite(null)}
        onConfirm={(reason) => { const s = overrideSite; setOverrideSite(null); doSaveSite(s, reason); }}
        entryLabel={`Sales for ${overrideSite || ""}`}
        dateLabel={date}
      />
    </div>
  );
}

const TotalBlock = ({ label, value, strong }) => (
  <div>
    <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{label}</div>
    <div className={`font-display font-black ${strong ? "text-2xl text-olive" : "text-xl text-slate-800"} tabular-nums`}>
      {formatINR(value)}
    </div>
  </div>
);

// ═══════════════════════════════════════════════════════════════════════════
// RECONCILE TAB — days awaiting receipts
// ═══════════════════════════════════════════════════════════════════════════
function ReconcileTab() {
  const [exportDate, setExportDate] = useState(todayISO());
  return (
    <div className="space-y-8" data-testid="reconcile-tab">
      <div className="bg-white border border-slate-200 rounded-2xl p-3 flex items-end gap-3 flex-wrap">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Export PDF for date</div>
          <input type="date" value={exportDate} onChange={(e) => setExportDate(e.target.value)}
                 data-testid="reconcile-export-date"
                 className="px-3 py-2 rounded-lg border border-slate-300 text-sm" />
        </div>
        <div className="ml-auto">
          <ExportPdfButton
            path={`/cashflow/exports/reconcile.pdf?date=${exportDate}`}
            filename={`VOW_Reconcile_${exportDate}.pdf`}
            testid="reconcile-export-pdf"
          />
        </div>
      </div>

      <section>
        <div className="flex items-center gap-2 mb-3">
          <Landmark size={14} strokeWidth={2.5} className="text-olive" />
          <h2 className="font-display font-bold text-base tracking-tight">Online receipts</h2>
          <div className="text-[11px] text-slate-500">Gateway settlement vs recorded online sales</div>
        </div>
        <OnlineReceiptsSection />
      </section>

      <section>
        <div className="flex items-center gap-2 mb-3">
          <ClipboardCheck size={14} strokeWidth={2.5} className="text-olive" />
          <h2 className="font-display font-bold text-base tracking-tight">Cash counts</h2>
          <div className="text-[11px] text-slate-500">Actual counted cash vs expected cash-in-hand</div>
        </div>
        <CountsSection />
      </section>
    </div>
  );
}

function OnlineReceiptsSection() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [focus, setFocus] = useState(null);  // { date, site } for drill-down

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/cashflow/pending-reconciliation");
      setRows(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (focus) {
    return (
      <ReconcileEditor
        date={focus.date}
        site={focus.site}
        onBack={() => setFocus(null)}
        onSaved={() => { load(); setFocus(null); }}
      />
    );
  }

  return (
    <div className="space-y-3" data-testid="reconcile-list">
      {loading && <div className="text-sm text-slate-500">Loading…</div>}
      {!loading && rows.length === 0 && (
        <div className="bg-white border border-slate-200 rounded-2xl p-6 text-center">
          <CheckCircle2 size={22} className="text-olive mx-auto mb-1.5" />
          <div className="text-sm font-semibold">All caught up</div>
          <div className="text-xs text-slate-500 mt-1">No days are waiting for receipts.</div>
        </div>
      )}
      {rows.map((row) => (
        <button
          key={`${row.sale_date}-${row.site}`}
          data-testid={`reconcile-row-${row.sale_date}-${row.site}`}
          onClick={() => setFocus({ date: row.sale_date, site: row.site })}
          className="w-full bg-white border border-slate-200 rounded-2xl p-4 flex items-center justify-between hover:border-olive/40 hover:bg-olive/5 transition text-left"
        >
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-400">
              {row.sale_date}
            </div>
            <div className="font-display font-bold text-lg mt-0.5">{row.site}</div>
            <div className="text-xs text-slate-500 mt-1">
              Online sales <span className="font-mono text-slate-700">{formatINR(row.online_total)}</span>
              {" · "}
              <span className="font-semibold">{row.lines_missing_receipt}</span> line{row.lines_missing_receipt !== 1 ? "s" : ""} awaiting receipt
              {row.receipts_started && <span className="ml-1 text-olive">· partial</span>}
            </div>
          </div>
          <ChevronRight size={18} className="text-slate-400" />
        </button>
      ))}
    </div>
  );
}

function ReconcileEditor({ date, site, onBack, onSaved }) {
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // { [revenuePoint || "__single__"]: value }
  const [drafts, setDrafts] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/cashflow/sales/${encodeURIComponent(date)}/${encodeURIComponent(site)}`);
      setDoc(r.data);
      const seed = {};
      for (const ln of r.data.lines || []) {
        const key = ln.revenue_point || "__single__";
        seed[key] = ln.received_online ?? "";
      }
      setDrafts(seed);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load");
      onBack();
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading || !doc) return <div className="text-sm text-slate-500">Loading…</div>;

  const submit = async () => {
    const receipts = [];
    for (const ln of doc.lines) {
      if ((ln.online || 0) <= 0) continue;
      const key = ln.revenue_point || "__single__";
      const v = drafts[key];
      if (v === "" || v === null || v === undefined) continue;
      receipts.push({ revenue_point: ln.revenue_point, received_online: Number(v) });
    }
    if (receipts.length === 0) {
      toast.error("Enter at least one receipt value");
      return;
    }
    setSaving(true);
    try {
      await api.patch(`/cashflow/sales/${encodeURIComponent(date)}/${encodeURIComponent(site)}/receipts`, { receipts });
      toast.success(`Receipts recorded · ${receipts.length} line(s)`);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid={`reconcile-editor-${date}-${site}`}>
      <button
        onClick={onBack}
        className="text-xs font-semibold text-slate-500 hover:text-slate-900 flex items-center gap-1"
      >
        ← Back
      </button>
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-400">{date}</div>
          <div className="font-display font-black text-xl mt-0.5">{site}</div>
        </div>
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-wider text-slate-500 bg-slate-50">
            <tr>
              <th className="text-left px-4 py-2 font-semibold">Revenue Point</th>
              <th className="text-right px-4 py-2 font-semibold">Sales · Online</th>
              <th className="text-right px-4 py-2 font-semibold w-40">Received</th>
              <th className="text-right px-4 py-2 font-semibold w-24">Diff</th>
            </tr>
          </thead>
          <tbody>
            {doc.lines.map((ln) => {
              const key = ln.revenue_point || "__single__";
              const displayLabel = ln.revenue_point || site;
              const online = ln.online || 0;
              const notApplicable = online <= 0;
              const val = drafts[key];
              const diffPreview = (val !== "" && val !== null && val !== undefined && online > 0)
                ? online - Number(val)
                : null;
              return (
                <tr key={key} className="border-t border-slate-100">
                  <td className="px-4 py-2 text-slate-700">{displayLabel}</td>
                  <td className="px-4 py-2 text-right font-mono text-slate-800">{formatINR(online)}</td>
                  <td className="px-4 py-2">
                    <NumberInput
                      value={val === "" || val === undefined ? 0 : Number(val)}
                      onChange={(v) => setDrafts((d) => ({ ...d, [key]: v }))}
                      testid={`reconcile-received-${key}`}
                      disabled={notApplicable}
                    />
                    {notApplicable && (
                      <div className="text-[10px] text-slate-400 mt-0.5 text-right">Cash-only line</div>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right font-mono text-xs">
                    {diffPreview === null ? "—" : (
                      <span className={Math.abs(diffPreview) > 1 ? "text-red-600 font-semibold" : "text-slate-600"}>
                        {formatINR(diffPreview)}
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div className="px-5 py-4 border-t border-slate-100 flex justify-end">
          <button
            data-testid="reconcile-save"
            onClick={submit}
            disabled={saving}
            className="bg-olive text-white px-5 py-2.5 rounded-lg text-sm font-semibold hover:bg-olive-700 disabled:opacity-40 transition"
          >
            {saving ? "Saving…" : "Record receipts"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// HISTORY TAB — past days with diffs + flags
// ═══════════════════════════════════════════════════════════════════════════
function HistoryTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams();
      if (from) q.append("from", from);
      if (to) q.append("to", to);
      const r = await api.get(`/cashflow/history${q.toString() ? "?" + q.toString() : ""}`);
      setRows(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-4" data-testid="history-tab">
      <div className="bg-white border border-slate-200 rounded-2xl p-4 flex items-end gap-3 flex-wrap">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">From</div>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="px-3 py-2 rounded-lg border border-slate-300 text-sm" data-testid="history-from" />
        </div>
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">To</div>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="px-3 py-2 rounded-lg border border-slate-300 text-sm" data-testid="history-to" />
        </div>
        <button onClick={load} className="bg-slate-900 text-white px-4 py-2 rounded-lg text-xs font-semibold hover:bg-slate-800">Apply</button>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-wider text-slate-500 bg-slate-50">
            <tr>
              <th className="text-left px-4 py-2 font-semibold">Date</th>
              <th className="text-left px-4 py-2 font-semibold">Site</th>
              <th className="text-right px-4 py-2 font-semibold">Cash</th>
              <th className="text-right px-4 py-2 font-semibold">Online</th>
              <th className="text-right px-4 py-2 font-semibold">Received</th>
              <th className="text-right px-4 py-2 font-semibold">Diff</th>
              <th className="text-center px-4 py-2 font-semibold">Status</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="p-6 text-center text-slate-500 text-sm">Loading…</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-slate-500 text-sm">No records.</td></tr>}
            {rows.map((r) => {
              const t = r.totals || {};
              const flagged = (t.flagged_lines || 0) > 0;
              const status = r.reconciled
                ? (flagged ? "flagged" : "reconciled")
                : (r.receipts_started ? "partial" : "pending");
              return (
                <tr key={`${r.sale_date}-${r.site}`} className="border-t border-slate-100" data-testid={`history-row-${r.sale_date}-${r.site}`}>
                  <td className="px-4 py-2 text-slate-500 text-xs">{r.sale_date}</td>
                  <td className="px-4 py-2 font-medium">{r.site}</td>
                  <td className="px-4 py-2 text-right font-mono text-xs">{formatINR(t.cash || 0)}</td>
                  <td className="px-4 py-2 text-right font-mono text-xs">{formatINR(t.online || 0)}</td>
                  <td className="px-4 py-2 text-right font-mono text-xs">{t.received_online !== null && t.received_online !== undefined ? formatINR(t.received_online) : "—"}</td>
                  <td className="px-4 py-2 text-right font-mono text-xs">
                    {t.diff === null || t.diff === undefined ? "—" : (
                      <span className={flagged ? "text-red-600 font-semibold" : "text-slate-600"}>{formatINR(t.diff)}</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-center">
                    <StatusPill status={status} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const StatusPill = ({ status }) => {
  const map = {
    pending:    { text: "Pending",    cls: "bg-slate-100 text-slate-600" },
    partial:    { text: "Partial",    cls: "bg-amber-100 text-amber-700" },
    reconciled: { text: "Reconciled", cls: "bg-olive/10 text-olive" },
    flagged:    { text: "Flagged",    cls: "bg-red-100 text-red-700",  Icon: AlertTriangle },
  };
  const info = map[status] || map.pending;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider ${info.cls}`}>
      {info.Icon && <info.Icon size={11} strokeWidth={2.5} />}
      {info.text}
    </span>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// Phase C2 — POSITION TAB (cash-in-hand per site + grand total + flagged list)
// ═══════════════════════════════════════════════════════════════════════════
function PositionTab() {
  const [asOf, setAsOf] = useState(todayISO());
  const [fromDate, setFromDate] = useState("");
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [focusSite, setFocusSite] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ to: asOf });
      if (fromDate) params.append("from", fromDate);
      const [uni, sitesR] = await Promise.all([
        api.get(`/cashflow/summary?${params.toString()}`),
        api.get(`/cashflow/position/summary?as_of=${asOf}`),
      ]);
      setSummary({ ...uni.data, sites: sitesR.data.sites, flagged_sites: sitesR.data.flagged_sites, tolerance: sitesR.data.tolerance, staleness_days: sitesR.data.staleness_days });
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [asOf, fromDate]);

  if (focusSite) {
    return (
      <PositionDetail
        site={focusSite}
        asOf={asOf}
        onBack={() => setFocusSite(null)}
      />
    );
  }

  const heads = summary ? Array.from(new Set([
    ...Object.keys(summary.money_in_by_head || {}),
    ...Object.keys(summary.money_out_by_head || {}),
  ])).sort() : [];

  return (
    <div className="space-y-4" data-testid="position-tab">
      {/* Filter card */}
      <div className="bg-white border border-slate-200 rounded-2xl p-4 flex items-end gap-4 flex-wrap">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">From (money-in/out)</div>
          <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)}
                 data-testid="summary-from" className="px-3 py-2 rounded-lg border border-slate-300 text-sm" />
        </div>
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">As of</div>
          <input type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)}
                 data-testid="position-asof" className="px-3 py-2 rounded-lg border border-slate-300 text-sm" />
        </div>
        <div className="ml-auto text-right">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Total Position (Cash + Bank)</div>
          <div className="font-display font-black text-3xl text-olive tabular-nums" data-testid="summary-total-position">
            {loading ? "…" : formatINR(summary?.total_position || 0)}
          </div>
          <div className="mt-2">
            <ExportPdfButton
              path={`/cashflow/exports/summary.pdf?${new URLSearchParams({ to: asOf, ...(fromDate ? { from: fromDate } : {}) }).toString()}`}
              filename={`VOW_Summary_${asOf}.pdf`}
              testid="summary-export-pdf"
              label="Export EOD PDF"
            />
          </div>
        </div>
      </div>

      {/* Cash vs Bank tiles */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="summary-cash-card">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Cash in Hand</div>
          <div className="font-display font-black text-2xl mt-1 tabular-nums" data-testid="summary-cash-total">
            {loading ? "…" : formatINR(summary?.cash_total || 0)}
          </div>
          <div className="text-[11px] text-slate-500 mt-1">Across {summary?.cash_by_site?.length || 0} sites</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="summary-bank-card">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Bank Balance</div>
          <div className="font-display font-black text-2xl mt-1 tabular-nums" data-testid="summary-bank-total">
            {loading ? "…" : formatINR(summary?.bank_total || 0)}
          </div>
          <div className="text-[11px] text-slate-500 mt-1">{summary?.bank_per_account?.length || 0} accounts + unallocated pool</div>
        </div>
      </div>

      {/* Money in vs out by head */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="summary-money-in">
          <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
            <div className="text-sm font-semibold">Money In · by Budget Head</div>
            <div className="font-mono font-black text-olive text-sm">{formatINR(summary?.money_in_total || 0)}</div>
          </div>
          <table className="w-full text-sm">
            <tbody>
              {loading && <tr><td className="p-4 text-slate-500 text-sm">Loading…</td></tr>}
              {!loading && heads.filter(h => (summary?.money_in_by_head || {})[h] > 0).length === 0 && (
                <tr><td className="p-4 text-slate-500 text-sm">No inflows in window.</td></tr>
              )}
              {!loading && heads.map((h) => {
                const v = (summary?.money_in_by_head || {})[h] || 0;
                if (!v) return null;
                return (
                  <tr key={"in-" + h} className="border-t border-slate-100" data-testid={`money-in-row-${h}`}>
                    <td className="px-5 py-2">{h}</td>
                    <td className="px-5 py-2 text-right font-mono">{formatINR(v)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="summary-money-out">
          <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
            <div className="text-sm font-semibold">Money Out · by Budget Head</div>
            <div className="font-mono font-black text-red-600 text-sm">{formatINR(summary?.money_out_total || 0)}</div>
          </div>
          <table className="w-full text-sm">
            <tbody>
              {loading && <tr><td className="p-4 text-slate-500 text-sm">Loading…</td></tr>}
              {!loading && heads.filter(h => (summary?.money_out_by_head || {})[h] > 0).length === 0 && (
                <tr><td className="p-4 text-slate-500 text-sm">No outflows in window.</td></tr>
              )}
              {!loading && heads.map((h) => {
                const v = (summary?.money_out_by_head || {})[h] || 0;
                if (!v) return null;
                return (
                  <tr key={"out-" + h} className="border-t border-slate-100" data-testid={`money-out-row-${h}`}>
                    <td className="px-5 py-2">{h}</td>
                    <td className="px-5 py-2 text-right font-mono">{formatINR(v)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {!loading && summary && summary.flagged_sites?.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-4" data-testid="position-flagged-callout">
          <div className="flex items-center gap-2 mb-1">
            <AlertTriangle size={14} className="text-red-600" />
            <div className="text-xs font-bold uppercase tracking-wider text-red-700">Attention needed</div>
          </div>
          <div className="text-xs text-red-800">
            <span className="font-semibold">{summary.flagged_sites.length}</span> site{summary.flagged_sites.length !== 1 ? "s" : ""} with variance beyond tolerance (₹{summary.tolerance}) or uncounted for over {summary.staleness_days} days: {summary.flagged_sites.join(", ")}
          </div>
        </div>
      )}

      {/* Per-site cash table (existing) */}
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100 text-sm font-semibold">Cash by Site</div>
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-wider text-slate-500 bg-slate-50">
            <tr>
              <th className="text-left px-4 py-2 font-semibold">Site</th>
              <th className="text-right px-4 py-2 font-semibold">Opening</th>
              <th className="text-right px-4 py-2 font-semibold">+ Sales</th>
              <th className="text-right px-4 py-2 font-semibold">− Expenses</th>
              <th className="text-right px-4 py-2 font-semibold">− Deposits</th>
              <th className="text-right px-4 py-2 font-semibold">± Adjust</th>
              <th className="text-right px-4 py-2 font-semibold">Expected</th>
              <th className="text-right px-4 py-2 font-semibold">Last Count</th>
              <th className="text-center px-4 py-2 font-semibold">Status</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={9} className="p-6 text-center text-slate-500 text-sm">Loading…</td></tr>}
            {!loading && (summary?.sites || []).map((s) => (
              <tr key={s.site} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                  data-testid={`position-row-${s.site}`}
                  onClick={() => setFocusSite(s.site)}>
                <td className="px-4 py-2 font-semibold">{s.site}</td>
                <td className="px-4 py-2 text-right font-mono text-xs text-slate-500">{formatINR(s.opening_balance)}</td>
                <td className="px-4 py-2 text-right font-mono text-xs text-slate-700">{formatINR(s.cash_sales)}</td>
                <td className="px-4 py-2 text-right font-mono text-xs text-slate-700">{formatINR(s.cash_expenses)}</td>
                <td className="px-4 py-2 text-right font-mono text-xs text-slate-700">{formatINR(s.deposits)}</td>
                <td className={`px-4 py-2 text-right font-mono text-xs ${(s.adjustments || 0) < 0 ? "text-red-600" : (s.adjustments || 0) > 0 ? "text-olive" : "text-slate-400"}`}>
                  {s.adjustments === 0 ? "—" : formatINR(s.adjustments)}
                </td>
                <td className="px-4 py-2 text-right font-mono font-bold text-slate-900">{formatINR(s.expected)}</td>
                <td className="px-4 py-2 text-right text-xs text-slate-500">
                  {s.last_count_date ? (
                    <div>
                      <div>{s.last_count_date}</div>
                      <div className={s.days_since_last_count > (summary?.staleness_days || 7) ? "text-red-600 font-semibold" : "text-slate-400"}>
                        {s.days_since_last_count}d ago
                      </div>
                    </div>
                  ) : (
                    <span className="text-red-600 font-semibold">Never</span>
                  )}
                </td>
                <td className="px-4 py-2 text-center">
                  {s.flagged
                    ? <StatusPill status={s.last_count_variance_flagged ? "flagged" : "pending"} />
                    : <StatusPill status="reconciled" />}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PositionDetailRow({ label, value, sign, muted }) {
  return (
    <tr className="border-t border-slate-100">
      <td className="px-4 py-2 text-slate-500 text-xs uppercase tracking-wider">{label}</td>
      <td className={`px-4 py-2 text-right font-mono ${muted ? "text-slate-500" : "text-slate-800"}`}>
        {sign}{formatINR(Math.abs(value || 0))}
      </td>
    </tr>
  );
}

function PositionDetail({ site, asOf, onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/cashflow/position?site=${encodeURIComponent(site)}&as_of=${asOf}`);
        setData(r.data);
      } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load"); }
      finally { setLoading(false); }
    })();
  }, [site, asOf]);

  if (loading || !data) return <div className="text-sm text-slate-500">Loading…</div>;

  return (
    <div className="space-y-4" data-testid={`position-detail-${site}`}>
      <button onClick={onBack} className="text-xs font-semibold text-slate-500 hover:text-slate-900">← Back</button>
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-400">As of {asOf}</div>
          <div className="font-display font-black text-2xl mt-0.5">{site}</div>
        </div>
        <table className="w-full text-sm">
          <tbody>
            <PositionDetailRow label={`Opening (${data.opening_date})`} value={data.opening_balance} sign="" muted />
            <PositionDetailRow label="+ Cash Sales" value={data.cash_sales} sign="+ " />
            <PositionDetailRow label="− Cash Expenses" value={data.cash_expenses} sign="− " />
            <PositionDetailRow label="− Deposits" value={data.deposits} sign="− " />
            {data.adjustments !== 0 && (
              <PositionDetailRow label="± Adjustments" value={data.adjustments} sign={data.adjustments >= 0 ? "+ " : "− "} />
            )}
            <tr className="border-t-2 border-slate-300 bg-olive/5">
              <td className="px-4 py-3 text-slate-900 font-semibold uppercase tracking-wider text-xs">Expected cash-in-hand</td>
              <td className="px-4 py-3 text-right font-mono font-black text-olive text-lg" data-testid="position-detail-expected">
                {formatINR(data.expected)}
              </td>
            </tr>
          </tbody>
        </table>
        <div className="px-5 py-3 border-t border-slate-100 text-xs text-slate-500 flex flex-wrap gap-4">
          {data.last_count ? (
            <>
              <div>Last count: <span className="font-semibold text-slate-800">{formatINR(data.last_count.actual_cash)}</span> on {data.last_count.count_date} ({data.days_since_last_count}d ago)</div>
              {data.last_count_variance_flagged && (
                <div className="text-red-600 font-semibold">Variance ₹{data.last_count.variance} — flagged</div>
              )}
            </>
          ) : (
            <div className="text-red-600 font-semibold">No count on record for this site</div>
          )}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// COUNTS TAB — record + list + absorb
// ═══════════════════════════════════════════════════════════════════════════
function CountsSection() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const dw = useDateWindow();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sites, setSites] = useState([]);
  // Draft
  const [site, setSite] = useState("");
  const [countDate, setCountDate] = useState(todayISO());
  const [actual, setActual] = useState(0);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [absorbing, setAbsorbing] = useState(null);   // count_id
  const [overrideOpen, setOverrideOpen] = useState(false);
  const dateState = dateWindowState(countDate, dw);

  const load = async () => {
    setLoading(true);
    try {
      const [cfg, r] = await Promise.all([
        api.get("/cashflow/config"),
        api.get("/cashflow/counts?limit=100"),
      ]);
      const configured = [...Object.keys(cfg.data.points_by_site || {}), ...(cfg.data.single_line_sites || [])];
      setSites(configured);
      if (!site && configured.length) setSite(configured[0]);
      setRows(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const doSubmit = async (overrideReason = null) => {
    if (!site) { toast.error("Pick a site"); return; }
    if (actual < 0) { toast.error("Actual cash must be ≥ 0"); return; }
    if (dateState.kind === "future") { toast.error("Future dates are not allowed."); return; }
    setSaving(true);
    try {
      await api.post("/cashflow/counts", {
        site, count_date: countDate, actual_cash: Number(actual), notes: notes || null,
        ...(overrideReason ? { admin_override: true, override_reason: overrideReason } : {}),
      });
      toast.success("Count recorded" + (overrideReason ? " (back-date override applied)" : ""));
      setActual(0); setNotes("");
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error((detail && typeof detail === "object" ? detail.message : detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const submit = () => {
    if (dateState.kind === "beyond") {
      if (!isAdmin) { toast.error("This date is beyond the back-date window. Ask an admin."); return; }
      setOverrideOpen(true); return;
    }
    doSubmit();
  };

  const absorb = async (row) => {
    const reason = window.prompt(`Absorb ₹${row.variance} variance for ${row.site} on ${row.count_date}?\n\nReason (required — audit-logged):`);
    if (!reason || !reason.trim()) return;
    setAbsorbing(row.count_id);
    try {
      await api.post(`/cashflow/counts/${row.count_id}/absorb`, { reason: reason.trim() });
      toast.success("Variance absorbed");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Absorb failed"); }
    finally { setAbsorbing(null); }
  };

  return (
    <div className="space-y-4" data-testid="counts-section">
      {/* Draft */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5">
        <div className="text-sm font-semibold mb-3">Record a count</div>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Site</div>
            <select value={site} onChange={(e) => setSite(e.target.value)} data-testid="counts-site"
                    className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm">
              {sites.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Count Date</div>
            <input type="date" value={countDate} onChange={(e) => setCountDate(e.target.value)}
                   max={dw.today}
                   data-testid="counts-date" className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm" />
            <DateWindowHint state={dateState} isAdmin={isAdmin} />
          </div>
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Actual Cash (₹)</div>
            <NumberInput value={actual} onChange={setActual} testid="counts-actual" />
          </div>
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Notes</div>
            <input type="text" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional"
                   data-testid="counts-notes" className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm" />
          </div>
        </div>
        <div className="flex justify-end mt-3">
          <button onClick={submit} disabled={saving || dateState.kind === "future" || (dateState.kind === "beyond" && !isAdmin)} data-testid="counts-submit"
                  className="bg-olive text-white px-5 py-2 rounded-lg text-xs font-semibold hover:bg-olive-700 disabled:opacity-40">
            {saving ? "Saving…" : dateState.kind === "beyond" && isAdmin ? "Record with override…" : "Record count"}
          </button>
        </div>
      </div>
      <OverrideReasonDialog
        open={overrideOpen}
        onCancel={() => setOverrideOpen(false)}
        onConfirm={(reason) => { setOverrideOpen(false); doSubmit(reason); }}
        entryLabel="This cash count"
        dateLabel={countDate}
      />

      {/* List */}
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-wider text-slate-500 bg-slate-50">
            <tr>
              <th className="text-left px-4 py-2 font-semibold">Date</th>
              <th className="text-left px-4 py-2 font-semibold">Site</th>
              <th className="text-right px-4 py-2 font-semibold">Expected</th>
              <th className="text-right px-4 py-2 font-semibold">Actual</th>
              <th className="text-right px-4 py-2 font-semibold">Variance</th>
              <th className="text-center px-4 py-2 font-semibold">Status</th>
              <th className="text-right px-4 py-2 font-semibold">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="p-6 text-center text-slate-500 text-sm">Loading…</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-slate-500 text-sm">No counts recorded yet.</td></tr>}
            {rows.map((row) => (
              <tr key={row.count_id} className="border-t border-slate-100" data-testid={`counts-row-${row.count_id}`}>
                <td className="px-4 py-2 text-xs text-slate-500">{row.count_date}</td>
                <td className="px-4 py-2 font-medium">{row.site}</td>
                <td className="px-4 py-2 text-right font-mono text-xs">{formatINR(row.expected_at_count)}</td>
                <td className="px-4 py-2 text-right font-mono text-xs font-semibold">{formatINR(row.actual_cash)}</td>
                <td className={`px-4 py-2 text-right font-mono text-xs ${row.flagged ? "text-red-600 font-bold" : "text-slate-500"}`}>{formatINR(row.variance)}</td>
                <td className="px-4 py-2 text-center">
                  {row.absorbed ? <StatusPill status="reconciled" /> :
                    row.flagged ? <StatusPill status="flagged" /> : <StatusPill status="reconciled" />}
                </td>
                <td className="px-4 py-2 text-right">
                  {!row.absorbed && Math.abs(row.variance) > 0 ? (
                    <button
                      onClick={() => absorb(row)}
                      disabled={absorbing === row.count_id}
                      data-testid={`counts-absorb-${row.count_id}`}
                      className="text-[11px] font-semibold text-red-600 hover:text-red-700 disabled:opacity-40"
                    >
                      {absorbing === row.count_id ? "Absorbing…" : "Absorb"}
                    </button>
                  ) : row.absorbed ? (
                    <span className="text-[10px] text-olive font-semibold uppercase tracking-wider">Absorbed</span>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// DEPOSITS TAB — record + list (site → bank)
// ═══════════════════════════════════════════════════════════════════════════
function DepositsTab() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const dw = useDateWindow();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sites, setSites] = useState([]);
  const [banks, setBanks] = useState([]);
  const [site, setSite] = useState("");
  const [depositDate, setDepositDate] = useState(todayISO());
  const [amount, setAmount] = useState(0);
  const [bank, setBank] = useState("");
  const [reference, setReference] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  // Phase C2.1 — live available-cash preview for the selected (site, date).
  const [available, setAvailable] = useState(null);
  const [availLoading, setAvailLoading] = useState(false);
  // Phase C3 — admin back-date override dialog.
  const [overrideOpen, setOverrideOpen] = useState(false);

  const dateState = dateWindowState(depositDate, dw);

  const load = async () => {
    setLoading(true);
    try {
      const [cfg, ba, r] = await Promise.all([
        api.get("/cashflow/config"),
        api.get("/cashflow/bank-accounts"),
        api.get("/cashflow/deposits"),
      ]);
      const configured = [...Object.keys(cfg.data.points_by_site || {}), ...(cfg.data.single_line_sites || [])];
      setSites(configured);
      if (!site && configured.length) setSite(configured[0]);
      const activeBanks = (ba.data || []).filter((b) => b.active);
      setBanks(activeBanks);
      if (!bank && activeBanks.length) setBank(activeBanks[0].id);
      setRows(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Refresh the available-cash preview whenever site or date changes.
  useEffect(() => {
    if (!site || !depositDate) { setAvailable(null); return; }
    let cancelled = false;
    setAvailLoading(true);
    api.get(`/cashflow/position?site=${encodeURIComponent(site)}&as_of=${depositDate}`)
      .then((r) => { if (!cancelled) setAvailable(r.data.expected); })
      .catch(() => { if (!cancelled) setAvailable(null); })
      .finally(() => { if (!cancelled) setAvailLoading(false); });
    return () => { cancelled = true; };
  }, [site, depositDate]);

  const overCap = available !== null && Number(amount) > 0 && Number(amount) > available + 1e-6;

  const doSubmit = async (overrideReason = null) => {
    if (!site || !bank) { toast.error("Site and bank required"); return; }
    if (amount <= 0) { toast.error("Amount must be > 0"); return; }
    if (dateState.kind === "future") { toast.error("Future dates are not allowed."); return; }
    if (overCap) {
      toast.error(`Cannot deposit ₹${Number(amount).toLocaleString("en-IN")} — ${site} expected cash is ₹${(available || 0).toLocaleString("en-IN")}.`);
      return;
    }
    setSaving(true);
    try {
      await api.post("/cashflow/deposits", {
        site, deposit_date: depositDate, amount: Number(amount),
        bank_account_id: bank,
        reference: reference || null, notes: notes || null,
        ...(overrideReason ? { admin_override: true, override_reason: overrideReason } : {}),
      });
      toast.success("Deposit recorded" + (overrideReason ? " (back-date override applied)" : ""));
      setAmount(0); setReference(""); setNotes("");
      await load();
      const r = await api.get(`/cashflow/position?site=${encodeURIComponent(site)}&as_of=${depositDate}`);
      setAvailable(r.data.expected);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code) {
        toast.error(detail.message || detail.code);
      } else {
        toast.error((typeof detail === "string" ? detail : null) || "Save failed");
      }
    } finally { setSaving(false); }
  };

  const submit = () => {
    if (dateState.kind === "beyond") {
      if (!isAdmin) {
        toast.error("This date is beyond the back-date window. Ask an admin.");
        return;
      }
      setOverrideOpen(true);
      return;
    }
    doSubmit();
  };

  return (
    <div className="space-y-4" data-testid="deposits-tab">
      <div className="flex items-center justify-end">
        <ExportPdfButton
          path={`/cashflow/exports/deposits.pdf${site ? `?site=${encodeURIComponent(site)}` : ""}`}
          filename={`VOW_Deposits_${todayISO()}.pdf`}
          testid="deposits-export-pdf"
          label="Export PDF"
        />
      </div>
      {banks.length === 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-3 text-xs text-amber-800">
          No active bank accounts. Add one under <b>Settings</b> to record deposits.
        </div>
      )}
      <div className="bg-white border border-slate-200 rounded-2xl p-5">
        <div className="text-sm font-semibold mb-3">Record a deposit</div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Site</div>
            <select value={site} onChange={(e) => setSite(e.target.value)} data-testid="deposits-site"
                    className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm">
              {sites.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Deposit Date</div>
            <input type="date" value={depositDate} onChange={(e) => setDepositDate(e.target.value)}
                   max={dw.today}
                   data-testid="deposits-date" className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm" />
            <DateWindowHint state={dateState} isAdmin={isAdmin} />
          </div>
          <div>
            <div className="flex items-center justify-between">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Amount (₹)</div>
              <div className="text-[10px] font-mono text-slate-400" data-testid="deposits-available">
                {availLoading ? "…" : available === null ? "" : `Available: ${formatINR(available)}`}
              </div>
            </div>
            <NumberInput value={amount} onChange={setAmount} testid="deposits-amount" />
            {overCap && (
              <div data-testid="deposits-over-cap-warning" className="mt-1 text-[11px] text-red-600 font-semibold">
                Exceeds expected cash at {site} (₹{(available || 0).toLocaleString("en-IN")}).
              </div>
            )}
          </div>
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Bank Account</div>
            <select value={bank} onChange={(e) => setBank(e.target.value)} data-testid="deposits-bank"
                    className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm" disabled={banks.length === 0}>
              {banks.map((b) => <option key={b.id} value={b.id}>{b.name}{b.last4 ? ` ····${b.last4}` : ""}</option>)}
            </select>
          </div>
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Reference</div>
            <input type="text" value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Slip #, txn ref"
                   data-testid="deposits-reference" className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm" />
          </div>
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Notes</div>
            <input type="text" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional"
                   data-testid="deposits-notes" className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm" />
          </div>
        </div>
        <div className="flex justify-end mt-3">
          <button onClick={submit} disabled={saving || banks.length === 0 || overCap || dateState.kind === "future" || (dateState.kind === "beyond" && !isAdmin)} data-testid="deposits-submit"
                  className="bg-olive text-white px-5 py-2 rounded-lg text-xs font-semibold hover:bg-olive-700 disabled:opacity-40">
            {saving ? "Saving…" : dateState.kind === "beyond" && isAdmin ? "Record with override…" : "Record deposit"}
          </button>
        </div>
      </div>
      <OverrideReasonDialog
        open={overrideOpen}
        onCancel={() => setOverrideOpen(false)}
        onConfirm={(reason) => { setOverrideOpen(false); doSubmit(reason); }}
        entryLabel="This deposit"
        dateLabel={depositDate}
      />

      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-wider text-slate-500 bg-slate-50">
            <tr>
              <th className="text-left px-4 py-2 font-semibold">Date</th>
              <th className="text-left px-4 py-2 font-semibold">Site</th>
              <th className="text-right px-4 py-2 font-semibold">Amount</th>
              <th className="text-left px-4 py-2 font-semibold">Bank</th>
              <th className="text-left px-4 py-2 font-semibold">Reference</th>
              <th className="text-left px-4 py-2 font-semibold">By</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="p-6 text-center text-slate-500 text-sm">Loading…</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-slate-500 text-sm">No deposits yet.</td></tr>}
            {rows.map((r) => (
              <tr key={r.deposit_id} className="border-t border-slate-100" data-testid={`deposits-row-${r.deposit_id}`}>
                <td className="px-4 py-2 text-xs text-slate-500">{r.deposit_date}</td>
                <td className="px-4 py-2 font-medium">{r.site}</td>
                <td className="px-4 py-2 text-right font-mono font-semibold">{formatINR(r.amount)}</td>
                <td className="px-4 py-2 text-xs">{r.bank_account_name}{r.bank_account_last4 ? ` ····${r.bank_account_last4}` : ""}</td>
                <td className="px-4 py-2 text-xs text-slate-500">{r.reference || "—"}</td>
                <td className="px-4 py-2 text-xs text-slate-500">{r.deposited_by_name}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// BANK TAB — per-account balances + ledger detail + manual entry form
// ═══════════════════════════════════════════════════════════════════════════
const BANK_ENTRY_TYPES = ["Deposit", "Withdrawal", "Bank Charges", "Interest", "Transfer-in", "Transfer-out"];
const BANK_ENTRY_SIGNS = {
  Deposit: 1, Interest: 1, "Transfer-in": 1,
  Withdrawal: -1, "Bank Charges": -1, "Transfer-out": -1,
};

function BankTab() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const dw = useDateWindow();
  const [banks, setBanks] = useState([]);
  const [heads, setHeads] = useState([]);
  const [ledger, setLedger] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState(todayISO());
  const [bankFilter, setBankFilter] = useState("");
  const [headFilter, setHeadFilter] = useState("");
  const [entryOpen, setEntryOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [ba, bh] = await Promise.all([
        api.get("/cashflow/bank-accounts"),
        api.get("/cashflow/budget-heads"),
      ]);
      setBanks(ba.data);
      setHeads(bh.data.heads || []);
      const params = new URLSearchParams({ to: toDate });
      if (fromDate) params.append("from", fromDate);
      if (bankFilter) params.append("bank_account_id", bankFilter);
      if (headFilter) params.append("budget_head", headFilter);
      const r = await api.get(`/cashflow/bank-ledger?${params.toString()}`);
      setLedger(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [fromDate, toDate, bankFilter, headFilter]);

  return (
    <div className="space-y-4" data-testid="bank-tab">
      {/* Header + entry button */}
      <div className="bg-white border border-slate-200 rounded-2xl p-4 flex items-end gap-3 flex-wrap">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">From</div>
          <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)}
                 data-testid="bank-from" className="px-3 py-2 rounded-lg border border-slate-300 text-sm" />
        </div>
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">To</div>
          <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)}
                 data-testid="bank-to" className="px-3 py-2 rounded-lg border border-slate-300 text-sm" />
        </div>
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Account</div>
          <select value={bankFilter} onChange={(e) => setBankFilter(e.target.value)}
                  data-testid="bank-account-filter"
                  className="px-3 py-2 rounded-lg border border-slate-300 text-sm">
            <option value="">All accounts + pool</option>
            {banks.map((b) => <option key={b.id} value={b.id}>{b.name}{b.last4 ? ` ····${b.last4}` : ""}</option>)}
          </select>
        </div>
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Budget head</div>
          <select value={headFilter} onChange={(e) => setHeadFilter(e.target.value)}
                  data-testid="bank-head-filter"
                  className="px-3 py-2 rounded-lg border border-slate-300 text-sm">
            <option value="">All heads</option>
            {heads.map((h) => <option key={h} value={h}>{h}</option>)}
          </select>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <ExportPdfButton
            path={`/cashflow/exports/bank.pdf?${new URLSearchParams({
              to: toDate,
              ...(fromDate ? { from: fromDate } : {}),
              ...(bankFilter ? { bank_account_id: bankFilter } : {}),
              ...(headFilter ? { budget_head: headFilter } : {}),
            }).toString()}`}
            filename={`VOW_Bank_${fromDate ? `${fromDate}_to_${toDate}` : toDate}.pdf`}
            testid="bank-export-pdf"
          />
          <button onClick={() => setEntryOpen(true)} data-testid="bank-add-entry"
                  className="bg-olive text-white px-4 py-2 rounded-lg text-xs font-semibold hover:bg-olive-700">
            + Manual entry
          </button>
        </div>
      </div>

      {/* Per-account balance tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {(ledger?.per_account || []).map((a) => (
          <div key={a.bank_account_id} className="bg-white border border-slate-200 rounded-2xl p-4"
               data-testid={`bank-acct-${a.bank_account_id}`}>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
              {a.bank_account_name}{a.bank_account_last4 ? ` ····${a.bank_account_last4}` : ""}
            </div>
            <div className="font-display font-black text-2xl mt-1 tabular-nums">{formatINR(a.balance)}</div>
            <div className="text-[11px] text-slate-500 mt-1">
              Opening ₹{formatINR(a.opening_balance)} · Deposits ₹{formatINR(a.deposits_in)} · Manual ₹{formatINR(a.manual_net)}
            </div>
          </div>
        ))}
        {ledger && (
          <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4"
               data-testid="bank-unallocated-pool">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Unallocated Pool</div>
            <div className="font-display font-black text-2xl mt-1 tabular-nums">{formatINR(ledger.unallocated_pool.net)}</div>
            <div className="text-[11px] text-slate-500 mt-1">
              Gateway ₹{formatINR(ledger.unallocated_pool.gateway_settlements_all_time)} − Bank/UPI expenses ₹{formatINR(ledger.unallocated_pool.bank_upi_expenses_all_time)}
            </div>
          </div>
        )}
        {ledger && (
          <div className="bg-olive/5 border border-olive/30 rounded-2xl p-4" data-testid="bank-combined-total">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Combined Bank Total</div>
            <div className="font-display font-black text-2xl mt-1 tabular-nums text-olive">{formatINR(ledger.combined_bank_total)}</div>
          </div>
        )}
      </div>

      {/* Ledger detail */}
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
          <div className="text-sm font-semibold">Ledger detail</div>
          <div className="text-[11px] text-slate-500">
            In {formatINR(ledger?.money_in_total || 0)} · Out {formatINR(ledger?.money_out_total || 0)}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-wider text-slate-500 bg-slate-50">
              <tr>
                <th className="text-left px-4 py-2 font-semibold">Date</th>
                <th className="text-left px-4 py-2 font-semibold">Type</th>
                <th className="text-left px-4 py-2 font-semibold">Account</th>
                <th className="text-left px-4 py-2 font-semibold">Budget Head</th>
                <th className="text-left px-4 py-2 font-semibold">Description</th>
                <th className="text-right px-4 py-2 font-semibold">Amount</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={6} className="p-6 text-center text-slate-500 text-sm">Loading…</td></tr>}
              {!loading && (ledger?.rows || []).length === 0 && (
                <tr><td colSpan={6} className="p-6 text-center text-slate-500 text-sm">No rows in the selected window.</td></tr>
              )}
              {!loading && (ledger?.rows || []).map((r) => (
                <tr key={r.source_id + r.kind} className="border-t border-slate-100" data-testid={`bank-row-${r.source_id}`}>
                  <td className="px-4 py-2 text-xs text-slate-500">{r.date}</td>
                  <td className="px-4 py-2 text-xs font-medium">{r.type}</td>
                  <td className="px-4 py-2 text-xs text-slate-500">{r.bank_account_name || "—"}</td>
                  <td className="px-4 py-2 text-xs text-slate-500">{r.budget_head || "—"}</td>
                  <td className="px-4 py-2 text-xs">{r.description}</td>
                  <td className={`px-4 py-2 text-right font-mono ${r.signed_amount >= 0 ? "text-olive" : "text-red-600"} font-semibold`}>
                    {r.signed_amount >= 0 ? "+ " : "− "}{formatINR(Math.abs(r.signed_amount))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <BankEntryDialog
        open={entryOpen}
        onClose={() => setEntryOpen(false)}
        onSaved={() => { setEntryOpen(false); load(); }}
        banks={banks}
        dw={dw}
        isAdmin={isAdmin}
      />
    </div>
  );
}

function BankEntryDialog({ open, onClose, onSaved, banks, dw, isAdmin }) {
  const [bankId, setBankId] = useState("");
  const [type, setType] = useState("Deposit");
  const [entryDate, setEntryDate] = useState(todayISO());
  const [amount, setAmount] = useState(0);
  const [counterparty, setCounterparty] = useState("");
  const [reference, setReference] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [overrideOpen, setOverrideOpen] = useState(false);
  const dateState = dateWindowState(entryDate, dw);
  const isTransfer = type === "Transfer-in" || type === "Transfer-out";

  useEffect(() => {
    if (open) {
      setType("Deposit"); setEntryDate(todayISO()); setAmount(0);
      setCounterparty(""); setReference(""); setNotes("");
      const firstActive = (banks || []).find((b) => b.active);
      setBankId(firstActive?.id || "");
    }
  }, [open, banks]);

  const doSubmit = async (overrideReason = null) => {
    if (!bankId) { toast.error("Pick an account"); return; }
    if (!type) { toast.error("Pick an entry type"); return; }
    if (!amount || Number(amount) <= 0) { toast.error("Amount must be > 0"); return; }
    if (isTransfer && !counterparty) { toast.error("Pick a counterparty account"); return; }
    if (dateState.kind === "future") { toast.error("Future dates are not allowed."); return; }
    setSaving(true);
    try {
      await api.post("/cashflow/bank-entries", {
        bank_account_id: bankId,
        entry_date: entryDate,
        type, amount: Number(amount),
        counterparty_bank_id: isTransfer ? counterparty : null,
        reference: reference || null,
        notes: notes || null,
        ...(overrideReason ? { admin_override: true, override_reason: overrideReason } : {}),
      });
      toast.success("Bank entry recorded" + (overrideReason ? " (back-date override)" : ""));
      onSaved && onSaved();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error((detail && typeof detail === "object" ? detail.message : detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const submit = () => {
    if (dateState.kind === "beyond") {
      if (!isAdmin) { toast.error("This date is beyond the back-date window. Ask an admin."); return; }
      setOverrideOpen(true); return;
    }
    doSubmit();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="bank-entry-dialog">
      <div className="bg-white rounded-2xl border border-slate-200 w-full max-w-lg p-5 space-y-3">
        <div>
          <div className="font-display font-black text-lg">Record a bank entry</div>
          <div className="text-xs text-slate-500 mt-1">Deposits, withdrawals, bank charges, interest and inter-account transfers.</div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Account</div>
            <select value={bankId} onChange={(e) => setBankId(e.target.value)}
                    data-testid="bank-entry-account"
                    className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm">
              {banks.filter((b) => b.active).map((b) => (
                <option key={b.id} value={b.id}>{b.name}{b.last4 ? ` ····${b.last4}` : ""}</option>
              ))}
            </select>
          </div>
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Type</div>
            <select value={type} onChange={(e) => setType(e.target.value)}
                    data-testid="bank-entry-type"
                    className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm">
              {BANK_ENTRY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Date</div>
            <input type="date" value={entryDate} onChange={(e) => setEntryDate(e.target.value)}
                   max={dw.today} data-testid="bank-entry-date"
                   className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm" />
            <DateWindowHint state={dateState} isAdmin={isAdmin} />
          </div>
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Amount (₹)</div>
            <NumberInput value={amount} onChange={setAmount} testid="bank-entry-amount" />
          </div>
          {isTransfer && (
            <div className="sm:col-span-2">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Counterparty account</div>
              <select value={counterparty} onChange={(e) => setCounterparty(e.target.value)}
                      data-testid="bank-entry-counterparty"
                      className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm">
                <option value="">— pick account —</option>
                {banks.filter((b) => b.active && b.id !== bankId).map((b) => (
                  <option key={b.id} value={b.id}>{b.name}{b.last4 ? ` ····${b.last4}` : ""}</option>
                ))}
              </select>
              <div className="text-[10px] text-slate-500 mt-1">The mirror leg on the counterparty account is written automatically.</div>
            </div>
          )}
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Reference</div>
            <input type="text" value={reference} onChange={(e) => setReference(e.target.value)}
                   placeholder="Optional" data-testid="bank-entry-ref"
                   className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm" />
          </div>
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Notes</div>
            <input type="text" value={notes} onChange={(e) => setNotes(e.target.value)}
                   placeholder="Optional" data-testid="bank-entry-notes"
                   className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm" />
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <button onClick={onClose} data-testid="bank-entry-cancel"
                  className="px-4 py-2 rounded-lg text-xs font-semibold text-slate-600 hover:bg-slate-100">Cancel</button>
          <button onClick={submit} disabled={saving || dateState.kind === "future" || (dateState.kind === "beyond" && !isAdmin)}
                  data-testid="bank-entry-submit"
                  className="bg-olive text-white px-5 py-2 rounded-lg text-xs font-semibold hover:bg-olive-700 disabled:opacity-40">
            {saving ? "Saving…" : dateState.kind === "beyond" && isAdmin ? "Record with override…" : "Record entry"}
          </button>
        </div>
        <OverrideReasonDialog
          open={overrideOpen}
          onCancel={() => setOverrideOpen(false)}
          onConfirm={(reason) => { setOverrideOpen(false); doSubmit(reason); }}
          entryLabel="This bank entry"
          dateLabel={entryDate}
        />
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// SETTINGS TAB — openings per site + bank accounts + tolerance/staleness
// ═══════════════════════════════════════════════════════════════════════════
function SettingsTab() {
  const [cfg, setCfg] = useState(null);
  const [openings, setOpenings] = useState({});
  const [banks, setBanks] = useState([]);
  const [tolerance, setTolerance] = useState("");
  const [staleness, setStaleness] = useState("");
  // Phase C3 — backdate window admin control.
  const [backdateDays, setBackdateDays] = useState("");
  // Phase v2.10 — Cash Flow start date (global read-side floor).
  const [startDate, setStartDate] = useState("");
  const [savingStart, setSavingStart] = useState(false);
  const [loading, setLoading] = useState(true);
  const [savingOp, setSavingOp] = useState(false);
  const [savingCfg, setSavingCfg] = useState(false);
  const [savingDw, setSavingDw] = useState(false);

  // Local edits for openings (site → {balance, as_of_date}).
  const [ops, setOps] = useState({});
  const [newBankName, setNewBankName] = useState("");
  const [newBankLast4, setNewBankLast4] = useState("");
  const [newBankOpening, setNewBankOpening] = useState("");
  const [newBankOpeningDate, setNewBankOpeningDate] = useState("");
  const [bankEdits, setBankEdits] = useState({});   // id → {opening_balance, opening_date}

  // Budget heads state.
  const [heads, setHeads] = useState([]);
  const [mapping, setMapping] = useState({});
  const [siteOverrides, setSiteOverrides] = useState({});
  const [savingBH, setSavingBH] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [c, op, ba, dw, bh, s, sd] = await Promise.all([
        api.get("/cashflow/config"),
        api.get("/cashflow/openings"),
        api.get("/cashflow/bank-accounts"),
        api.get("/date-window/config"),
        api.get("/cashflow/budget-heads"),
        api.get("/sites"),
        api.get("/cashflow/start-date"),
      ]);
      setCfg({ ...c.data, categories: s.data.categories || [] });
      setTolerance(String(c.data.diff_tolerance));
      setStaleness(String(c.data.staleness_days));
      setBackdateDays(String(dw.data.backdate_days));
      setStartDate(sd.data.date || "");
      setOpenings(op.data.opening_by_site || {});
      setOps(op.data.opening_by_site || {});
      setBanks(ba.data);
      const seed = {};
      (ba.data || []).forEach((b) => { seed[b.id] = { opening_balance: b.opening_balance ?? 0, opening_date: b.opening_date || "" }; });
      setBankEdits(seed);
      setHeads(bh.data.heads || []);
      setMapping(bh.data.mapping || {});
      setSiteOverrides(bh.data.site_overrides || {});
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const saveBackdate = async () => {
    const v = Number(backdateDays);
    if (!Number.isFinite(v) || v < 0 || v > 365) { toast.error("Days must be 0..365"); return; }
    setSavingDw(true);
    try {
      await api.put("/admin/date-window/config", { backdate_days: v });
      toast.success("Back-date window updated");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSavingDw(false); }
  };

  const saveStart = async () => {
    if (!startDate || !/^\d{4}-\d{2}-\d{2}$/.test(startDate)) {
      toast.error("Pick a valid start date"); return;
    }
    setSavingStart(true);
    try {
      const r = await api.put("/admin/cashflow/start-date", { date: startDate });
      toast.success(`Cash Flow start date set to ${r.data.date}`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSavingStart(false); }
  };

  const saveOpenings = async () => {
    // Diff and only send changed entries
    const payload = [];
    for (const [site, v] of Object.entries(ops)) {
      const prev = openings[site];
      if (!prev || prev.balance !== v.balance || prev.as_of_date !== v.as_of_date) {
        if (v.balance !== "" && v.as_of_date) {
          payload.push({ site, balance: Number(v.balance), as_of_date: v.as_of_date });
        }
      }
    }
    if (payload.length === 0) { toast.info("No changes"); return; }
    setSavingOp(true);
    try {
      await api.put("/admin/cashflow/openings", { openings: payload });
      toast.success(`Openings saved · ${payload.length} site(s)`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSavingOp(false); }
  };

  const saveCfg = async () => {
    setSavingCfg(true);
    try {
      await api.put("/admin/cashflow/config", {
        diff_tolerance: Number(tolerance),
        staleness_days: Number(staleness),
      });
      toast.success("Config saved");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSavingCfg(false); }
  };

  const addBank = async () => {
    if (!newBankName.trim()) { toast.error("Bank name required"); return; }
    try {
      await api.post("/admin/cashflow/bank-accounts", {
        name: newBankName.trim(),
        last4: newBankLast4.trim() || null,
        active: true,
        opening_balance: newBankOpening === "" ? 0 : Number(newBankOpening),
        opening_date: newBankOpeningDate || null,
      });
      toast.success("Bank added");
      setNewBankName(""); setNewBankLast4(""); setNewBankOpening(""); setNewBankOpeningDate("");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const toggleBank = async (b) => {
    try {
      await api.patch(`/admin/cashflow/bank-accounts/${b.id}`, { active: !b.active });
      toast.success(b.active ? "Deactivated" : "Activated");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const saveBankOpening = async (b) => {
    const edit = bankEdits[b.id] || {};
    try {
      await api.patch(`/admin/cashflow/bank-accounts/${b.id}`, {
        opening_balance: edit.opening_balance === "" ? 0 : Number(edit.opening_balance),
        opening_date: edit.opening_date || null,
      });
      toast.success("Bank opening saved");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const saveBudgetHeads = async () => {
    setSavingBH(true);
    try {
      await api.put("/admin/cashflow/budget-heads", {
        heads, mapping, site_overrides: siteOverrides,
      });
      toast.success("Budget heads saved");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSavingBH(false); }
  };

  if (loading || !cfg) return <div className="text-sm text-slate-500">Loading…</div>;

  const allSites = [...Object.keys(cfg.points_by_site || {}), ...(cfg.single_line_sites || [])];
  const allCategories = cfg.categories || [];

  return (
    <div className="space-y-6" data-testid="settings-tab">
      {/* Tolerance + staleness */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5">
        <div className="text-sm font-semibold mb-3">Tolerances</div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Diff tolerance (₹)</div>
            <input type="number" min="0" step="0.5" value={tolerance} onChange={(e) => setTolerance(e.target.value)}
                   onWheel={blurOnWheel}
                   onKeyDown={(e) => { if (e.key === "ArrowUp" || e.key === "ArrowDown") e.preventDefault(); }}
                   data-testid="settings-tolerance" className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm" />
            <div className="text-[10px] text-slate-500 mt-1">Applies to sales-vs-receipt Diff AND count variance flag.</div>
          </div>
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Staleness threshold (days)</div>
            <input type="number" min="1" max="365" value={staleness} onChange={(e) => setStaleness(e.target.value)}
                   onWheel={blurOnWheel}
                   onKeyDown={(e) => { if (e.key === "ArrowUp" || e.key === "ArrowDown") e.preventDefault(); }}
                   data-testid="settings-staleness" className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm" />
            <div className="text-[10px] text-slate-500 mt-1">Sites uncounted longer than this are flagged.</div>
          </div>
          <div className="flex items-end">
            <button onClick={saveCfg} disabled={savingCfg} data-testid="settings-save-cfg"
                    className="bg-olive text-white px-5 py-2 rounded-lg text-xs font-semibold hover:bg-olive-700 disabled:opacity-40">
              {savingCfg ? "Saving…" : "Save tolerances"}
            </button>
          </div>
        </div>
      </div>

      {/* Backdate window */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="settings-backdate-card">
        <div className="text-sm font-semibold mb-1">Back-date window</div>
        <div className="text-[11px] text-slate-500 mb-3">
          How many days in the past a non-admin may record any financial entry
          (daily sales, receipts, cash counts, deposits, payments, purchase time on requests).
          Beyond this an admin override with a mandatory reason is required. Future dates are always rejected.
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Days</div>
            <input type="number" min="0" max="365" value={backdateDays}
                   onChange={(e) => setBackdateDays(e.target.value)}
                   onWheel={blurOnWheel}
                   onKeyDown={(e) => { if (e.key === "ArrowUp" || e.key === "ArrowDown") e.preventDefault(); }}
                   data-testid="settings-backdate-days"
                   className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm" />
          </div>
          <div className="flex items-end">
            <button onClick={saveBackdate} disabled={savingDw} data-testid="settings-save-backdate"
                    className="bg-olive text-white px-5 py-2 rounded-lg text-xs font-semibold hover:bg-olive-700 disabled:opacity-40">
              {savingDw ? "Saving…" : "Save back-date window"}
            </button>
          </div>
        </div>
      </div>

      {/* Cash Flow start date — global read-side floor */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="settings-cashflow-start-card">
        <div className="text-sm font-semibold mb-1">Cash Flow start date</div>
        <div className="text-[11px] text-slate-500 mb-3">
          Position math, bank ledger, unified summary and the pending-reconciliation list
          only count transactions dated <b>on or after</b> this date. Anything before it
          stays untouched in its underlying record (Sales, Deposits, Bank entries, Payments)
          and remains visible in those tabs — but does not contribute to the Cash Flow position.
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Start date</div>
            <input type="date" value={startDate}
                   onChange={(e) => setStartDate(e.target.value)}
                   data-testid="settings-cashflow-start-date"
                   className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm" />
          </div>
          <div className="flex items-end">
            <button onClick={saveStart} disabled={savingStart}
                    data-testid="settings-save-cashflow-start"
                    className="bg-olive text-white px-5 py-2 rounded-lg text-xs font-semibold hover:bg-olive-700 disabled:opacity-40">
              {savingStart ? "Saving…" : "Save start date"}
            </button>
          </div>
        </div>
      </div>

      {/* Openings per site */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-semibold">Opening cash by site</div>
          <button onClick={saveOpenings} disabled={savingOp} data-testid="settings-save-openings"
                  className="bg-olive text-white px-4 py-1.5 rounded-lg text-xs font-semibold hover:bg-olive-700 disabled:opacity-40">
            {savingOp ? "Saving…" : "Save openings"}
          </button>
        </div>
        <div className="text-[11px] text-slate-500 mb-3">Set once per site as of a start date. Cash calculations begin from this baseline; sales/expenses/deposits recorded on or before that date are ignored.</div>
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-wider text-slate-500 bg-slate-50">
            <tr>
              <th className="text-left px-3 py-2 font-semibold">Site</th>
              <th className="text-left px-3 py-2 font-semibold">Opening Balance</th>
              <th className="text-left px-3 py-2 font-semibold">As-of date</th>
            </tr>
          </thead>
          <tbody>
            {allSites.map((s) => (
              <tr key={s} className="border-t border-slate-100">
                <td className="px-3 py-2 font-medium">{s}</td>
                <td className="px-3 py-2">
                  <input type="number" min="0" step="1"
                         onWheel={blurOnWheel}
                         onKeyDown={(e) => { if (e.key === "ArrowUp" || e.key === "ArrowDown") e.preventDefault(); }}
                         value={ops[s]?.balance ?? ""}
                         onChange={(e) => setOps((o) => ({ ...o, [s]: { ...o[s], balance: e.target.value === "" ? "" : Number(e.target.value) } }))}
                         data-testid={`settings-opening-balance-${s}`}
                         className="w-40 px-2 py-1 rounded-lg border border-slate-300 text-sm" />
                </td>
                <td className="px-3 py-2">
                  <input type="date"
                         value={ops[s]?.as_of_date || ""}
                         onChange={(e) => setOps((o) => ({ ...o, [s]: { ...o[s], as_of_date: e.target.value } }))}
                         data-testid={`settings-opening-date-${s}`}
                         className="px-2 py-1 rounded-lg border border-slate-300 text-sm" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Bank accounts */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5">
        <div className="text-sm font-semibold mb-3">Bank accounts</div>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-3">
          <input type="text" placeholder="Account name (e.g. ICICI Current)"
                 value={newBankName} onChange={(e) => setNewBankName(e.target.value)}
                 data-testid="settings-new-bank-name"
                 className="px-3 py-2 rounded-lg border border-slate-300 text-sm" />
          <input type="text" placeholder="Last 4 (opt)" maxLength={4}
                 value={newBankLast4} onChange={(e) => setNewBankLast4(e.target.value)}
                 data-testid="settings-new-bank-last4"
                 className="px-3 py-2 rounded-lg border border-slate-300 text-sm" />
          <input type="number" placeholder="Opening balance"
                 onWheel={blurOnWheel} value={newBankOpening}
                 onChange={(e) => setNewBankOpening(e.target.value)}
                 data-testid="settings-new-bank-opening"
                 className="px-3 py-2 rounded-lg border border-slate-300 text-sm" />
          <input type="date" value={newBankOpeningDate}
                 onChange={(e) => setNewBankOpeningDate(e.target.value)}
                 data-testid="settings-new-bank-opening-date"
                 className="px-3 py-2 rounded-lg border border-slate-300 text-sm" />
          <button onClick={addBank} data-testid="settings-add-bank"
                  className="bg-slate-900 text-white px-4 py-2 rounded-lg text-xs font-semibold hover:bg-slate-800">
            Add bank account
          </button>
        </div>
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-wider text-slate-500 bg-slate-50">
            <tr>
              <th className="text-left px-3 py-2 font-semibold">Name</th>
              <th className="text-left px-3 py-2 font-semibold">Last 4</th>
              <th className="text-left px-3 py-2 font-semibold">Opening Balance</th>
              <th className="text-left px-3 py-2 font-semibold">Opening Date</th>
              <th className="text-center px-3 py-2 font-semibold">Active</th>
              <th className="text-right px-3 py-2 font-semibold">Save</th>
            </tr>
          </thead>
          <tbody>
            {banks.length === 0 && <tr><td colSpan={6} className="p-4 text-center text-slate-500 text-sm">No bank accounts yet.</td></tr>}
            {banks.map((b) => (
              <tr key={b.id} className="border-t border-slate-100" data-testid={`settings-bank-row-${b.id}`}>
                <td className="px-3 py-2 font-medium">{b.name}</td>
                <td className="px-3 py-2 text-xs text-slate-500">{b.last4 ? `····${b.last4}` : "—"}</td>
                <td className="px-3 py-2">
                  <input type="number" min="0" step="1"
                         onWheel={blurOnWheel}
                         value={bankEdits[b.id]?.opening_balance ?? ""}
                         onChange={(e) => setBankEdits((s) => ({ ...s, [b.id]: { ...s[b.id], opening_balance: e.target.value === "" ? "" : Number(e.target.value) } }))}
                         data-testid={`settings-bank-opening-${b.id}`}
                         className="w-32 px-2 py-1 rounded-lg border border-slate-300 text-sm" />
                </td>
                <td className="px-3 py-2">
                  <input type="date"
                         value={bankEdits[b.id]?.opening_date || ""}
                         onChange={(e) => setBankEdits((s) => ({ ...s, [b.id]: { ...s[b.id], opening_date: e.target.value } }))}
                         data-testid={`settings-bank-opening-date-${b.id}`}
                         className="px-2 py-1 rounded-lg border border-slate-300 text-sm" />
                </td>
                <td className="px-3 py-2 text-center">
                  <button onClick={() => toggleBank(b)}
                          data-testid={`settings-bank-toggle-${b.id}`}
                          className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider ${b.active ? "bg-olive/10 text-olive" : "bg-slate-100 text-slate-500"}`}>
                    {b.active ? "Active" : "Inactive"}
                  </button>
                </td>
                <td className="px-3 py-2 text-right">
                  <button onClick={() => saveBankOpening(b)}
                          data-testid={`settings-bank-save-${b.id}`}
                          className="bg-olive text-white px-3 py-1 rounded-lg text-[10px] font-semibold hover:bg-olive-700">
                    Save
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Budget heads mapping */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="settings-budget-heads-card">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-sm font-semibold">Budget heads · Category → Head mapping</div>
            <div className="text-[11px] text-slate-500 mt-0.5">Site override always wins. Categories left as “Unmapped” surface in a distinct row on the ledger.</div>
          </div>
          <button onClick={saveBudgetHeads} disabled={savingBH} data-testid="settings-save-budget-heads"
                  className="bg-olive text-white px-4 py-1.5 rounded-lg text-xs font-semibold hover:bg-olive-700 disabled:opacity-40">
            {savingBH ? "Saving…" : "Save budget heads"}
          </button>
        </div>
        <div className="text-[11px] text-slate-500 mb-2">Heads: <span className="font-mono">{heads.join(" · ") || "—"}</span></div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-2">Category → Head</div>
            <div className="max-h-80 overflow-y-auto border border-slate-100 rounded-lg">
              <table className="w-full text-sm">
                <tbody>
                  {allCategories.map((cat) => (
                    <tr key={cat} className="border-t border-slate-100 first:border-t-0">
                      <td className="px-3 py-1.5">{cat}</td>
                      <td className="px-3 py-1.5">
                        <select value={mapping[cat] || ""}
                                onChange={(e) => setMapping((m) => ({ ...m, [cat]: e.target.value }))}
                                data-testid={`bh-map-${cat}`}
                                className="w-full px-2 py-1 rounded-lg border border-slate-300 text-xs">
                          <option value="">— unmapped —</option>
                          {heads.map((h) => <option key={h} value={h}>{h}</option>)}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-2">Site override → Head (wins over category)</div>
            <div className="border border-slate-100 rounded-lg">
              <table className="w-full text-sm">
                <tbody>
                  {allSites.map((s) => (
                    <tr key={s} className="border-t border-slate-100 first:border-t-0">
                      <td className="px-3 py-1.5">{s}</td>
                      <td className="px-3 py-1.5">
                        <select value={siteOverrides[s] || ""}
                                onChange={(e) => setSiteOverrides((so) => {
                                  const next = { ...so };
                                  if (!e.target.value) delete next[s]; else next[s] = e.target.value;
                                  return next;
                                })}
                                data-testid={`bh-site-${s}`}
                                className="w-full px-2 py-1 rounded-lg border border-slate-300 text-xs">
                          <option value="">— no override —</option>
                          {heads.map((h) => <option key={h} value={h}>{h}</option>)}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

